/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/ui/Input.tsx
 * PURPOSE: Sprint 34 - Accessible Input Component with Vietnamese F&B Design
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-01-26
 * COMPLIANCE: SDLC 6.3.1 + WCAG 2.1 Level AA + Sprint 34 Story 3
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Rule 4: Tier-3 Code Review) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 8: Quality Gates (Accessibility)
 *
 * @business-context
 * - Domain: Vietnamese F&B Form Inputs, User Data Entry
 * - Impact: User Experience, Data Quality, Accessibility Compliance
 * - Stakeholders: End Users, Accessibility Team, QA Team
 *
 * @technical-architecture
 * - Layer: Frontend Presentation Layer, UI Components
 * - Pattern: Controlled Component, forwardRef, ARIA Best Practices
 * - Dependencies: React 18, TypeScript, CSS Modules
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Sprint Plan: docs/08-collaborate/06-Project-Management/SPRINT-34-EXECUTION-PLAN.md
 * - CSS Module: frontend/src/components/ui/Input.module.css
 */

import React, { forwardRef } from 'react';
import styles from './Input.module.css';
import { cn } from '@/utils/cn';

/**
 * Input Component Props
 *
 * @interface InputProps
 * @extends React.InputHTMLAttributes<HTMLInputElement>
 * @property {string} label - Input label (required for accessibility)
 * @property {string} [error] - Error message (displayed below input)
 * @property {string} [hint] - Hint text (displayed below input when no error)
 * @property {React.ReactNode} [leftIcon] - Icon displayed on the left
 * @property {React.ReactNode} [rightIcon] - Icon displayed on the right
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * Input Component
 *
 * Accessible input component with Vietnamese F&B design and WCAG 2.1 AA compliance.
 *
 * **Features**:
 * - Automatic ID generation for accessibility
 * - aria-describedby linking to error/hint messages
 * - aria-invalid for error state
 * - Required indicator (red asterisk)
 * - Left and right icon support
 * - Focus visible styles (Coffee color)
 * - Error role="alert" for screen readers
 * - Vietnamese design tokens (no hardcoded Tailwind)
 *
 * **WCAG 2.1 AA Compliance**:
 * - Success Criterion 1.3.1: Info and Relationships (label association)
 * - Success Criterion 3.3.1: Error Identification (aria-invalid, role="alert")
 * - Success Criterion 3.3.2: Labels or Instructions (hint text)
 * - Success Criterion 4.1.2: Name, Role, Value (proper ARIA attributes)
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email Address"
 *   type="email"
 *   required
 *   hint="We'll never share your email with anyone else."
 *   leftIcon={<EnvelopeIcon />}
 * />
 *
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password must be at least 8 characters"
 *   required
 * />
 * ```
 *
 * @param props - InputProps
 * @param ref - Forwarded ref to the input element
 * @returns Accessible input component
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, rightIcon, className, id, ...props }, ref) => {
    // Generate unique IDs for accessibility
    const inputId = id || `input-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const hintId = hint ? `${inputId}-hint` : undefined;

    // Compute aria-describedby (error takes priority over hint)
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
      <div className={cn(styles.inputGroup, error && styles.hasError, className)}>
        {/* Label with required indicator */}
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {props.required && (
            <span className={styles.required} aria-hidden="true">
              {' '}*
            </span>
          )}
        </label>

        {/* Input wrapper with icons */}
        <div className={styles.inputWrapper}>
          {leftIcon && (
            <span className={styles.iconLeft} aria-hidden="true">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              styles.input,
              leftIcon && styles.hasLeftIcon,
              rightIcon && styles.hasRightIcon
            )}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            {...props}
          />
          {rightIcon && (
            <span className={styles.iconRight} aria-hidden="true">
              {rightIcon}
            </span>
          )}
        </div>

        {/* Hint text (only shown when no error) */}
        {hint && !error && (
          <p id={hintId} className={styles.hint}>
            {hint}
          </p>
        )}

        {/* Error message (role="alert" for screen readers) */}
        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
